import { HttpStatusCode } from "axios";
import { useIsPresent } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import {
  SetterOrUpdater,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";

import {
  apiDataFetchingErrorsAtom,
  ApiException,
  archiveRelationErrorAtomFamily,
  ComputedDataKey,
  createErrorResult,
  createErrorResultFromException,
  createErrorResultFromTranslatableItem,
  createSuccessResult,
  CrudApi,
  DELETE_ENTITY_WITH_RELATION_PAYLOAD,
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  EditFormMode,
  ENTITY_DELETE_RELATIONS_CONFIRM_LIST,
  errors,
  errorsCrudContentAtomFamily,
  getProhibitedToDuplicateFields,
  getResolvedDuplicatedDto,
  IApiError,
  IRideDto,
  isEntityServerSideFiltered,
  isNeedToFetchTableRowsAF,
  isRideEntity,
  isTranslatableItem,
  IValidationError,
  loadingEditFormContentAtomFamily,
  Log,
  logBoolean,
  NO_VALUE_DISPLAY,
  normalizeErrors,
  normalizeReferencedEntitiesErrors,
  prohibitedToDuplicateFieldsSF,
  RequestBodyModel,
  RydeAppUnit,
  RydeUiUnit,
  SimpleResult,
  transformApiCrudErrorForToast,
  translateInfoItem,
  warnAndToastException,
} from "@ryde/shared";

import { useDropdownSourceContext } from "../../contexts";
import { useCrudNavigate, useSimpleNavigation } from "../../navigation";
import { useProfileActions } from "../auth-profile";
import { useEditFormCacheOnUnmount } from "../dynamic-forms/use-edit-form-cache-on-unmount";
import { useStarredRows } from "../dynamic-tables";
import {
  useRideAreasSnapshot,
  useRideDuplicate,
  useRideMetadataSnapshot,
} from "../rides";
import { useHighlightEdited } from "../use-highlight-edited";
import { refreshDropdownItemsForEntity, useSchema } from "./parts";
import { useSetDropdownAtomFamily } from "./use-dropdown-atom-family-utils";

interface IUseDrawerEntityEditArguments {
  entity: RydeUiUnit;
  idCopyFrom?: number;
  id?: number;
  mode?: EditFormMode;
  /** Ride duplicate-from-drawer: return ride vs plain duplicate (see IRideEditFormDrawerProps). */
  duplicateReverseStations?: boolean;
}

export interface IEditFormData<T> {
  isEdit?: boolean;
  isDuplicate?: boolean;
  dto?: T;
  initialDataSnapshotDto?: T;
  save?: (obj: Partial<T>) => Promise<SimpleResult<T>>;
  archive?: () => Promise<SimpleResult<RequestBodyModel>>;
  archiveWithRelation?: () => Promise<SimpleResult<RequestBodyModel>>;
  onEditFormClose?: () => void;
  fetchDto?: (curId: number) => Promise<SimpleResult<RequestBodyModel>>;
  processDuplicationDto: (
    dtoFromApi: RequestBodyModel,
  ) => Record<string, unknown>;
  setDto: SetterOrUpdater<T | undefined>;
}

export const useCrudEdit = <T extends RequestBodyModel>({
  entity,
  idCopyFrom,
  mode = EditFormMode.CrudRoute,
  id,
  duplicateReverseStations,
}: IUseDrawerEntityEditArguments): IEditFormData<T> => {
  const [dto, setDto] = useRecoilState(editFormDataAF([entity, mode]));
  const { t, i18n } = useTranslation();
  const { setHighlightedRows } = useHighlightEdited(entity);
  const { removeStarredIds } = useStarredRows(entity);
  const { fetchSchema } = useSchema(entity);
  const { navigateToList } = useCrudNavigate(entity as RydeAppUnit);
  const { getDropdownItems } = useDropdownSourceContext();
  const { updateMetadataResolve } = useSetDropdownAtomFamily();
  const { processRideDuplicateDto } = useRideDuplicate();
  const { navigatePageNotFound } = useSimpleNavigation();

  /** Avoids infinite refetch when edit drawer gets 404 and dto is cleared. */
  const suppressEntityFetchAfterNotFoundRef = useRef(false);

  const { afterSaveCallback, afterFetchCrudDtoCallback } = useProfileActions();

  const isDuplicate = Boolean(idCopyFrom);
  const isEdit = Boolean(id) && !idCopyFrom;
  /* false while a CRUD route page plays its exit animation: the next route already owns the
     shared editFormDataAF (e.g. edit -> duplicate), so the leaving page must not fetch or rewrite it */
  const isRoutePresent = useIsPresent();
  useEditFormCacheOnUnmount({ entity, mode, id, idCopyFrom });

  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(entity),
  );
  const setArchiveRelationError = useSetRecoilState(
    archiveRelationErrorAtomFamily(entity),
  );
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const [isLoading, setLoading] = useRecoilState(
    loadingEditFormContentAtomFamily(entity),
  );
  const setIsNeedFetch = useSetRecoilState(isNeedToFetchTableRowsAF(entity));
  const setInitialDataSnapshotDto = useSetRecoilState(
    editFormDataInitialSnapshotAF([entity, mode]),
  );
  const { updateRidePreviousAreasSnapshot } = useRideAreasSnapshot();
  const { updateRidePreviousMetadataSnapshot } = useRideMetadataSnapshot();
  const prohibitedToDuplicateControls = useRecoilValue(
    prohibitedToDuplicateFieldsSF(entity),
  );
  const [, startTransition] = useTransition();

  const processDuplicationDto = useEvent((dtoFromApi: RequestBodyModel) => {
    let duplicatedDto: Record<string, unknown>;

    if (!isRideEntity(entity)) {
      const prohibitedToDuplicateFields = getProhibitedToDuplicateFields({
        dto: dtoFromApi,
        controls: prohibitedToDuplicateControls,
      });

      const baseDto: Partial<RequestBodyModel> = {
        ...dtoFromApi,
        id: undefined,
        oldId: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        [ComputedDataKey]: {
          ...(dtoFromApi?.[ComputedDataKey] || {}),
          isNewTemplate: false,
          isDuplicate: true,
        },
      };

      duplicatedDto = getResolvedDuplicatedDto({
        dto: baseDto,
        prohibitedToDuplicateFields,
      });
    } else {
      duplicatedDto = processRideDuplicateDto({
        rideDto: dtoFromApi,
        reverseStations: duplicateReverseStations ?? false,
      });
    }

    return duplicatedDto;
  });

  const processDtoFromApi = useEvent(
    (dtoFromApi: RequestBodyModel): RequestBodyModel => {
      return {
        ...dtoFromApi,
        [ComputedDataKey]: {
          ...(dtoFromApi?.[ComputedDataKey] || {}),
          isNewTemplate: false,
          isDuplicate: false,
        },
      };
    },
  );

  const onEditFormClose = useCallback(() => {
    setCrudServerErrors(undefined);

    if (mode === EditFormMode.CrudRoute) {
      navigateToList(true);
    }
  }, [mode, navigateToList, setCrudServerErrors]);

  const isInvalidCrudEditDtoId = useMemo((): boolean => {
    return isEdit && (!id || Number(id) < 1);
  }, [id, isEdit]);

  useEffect(() => {
    suppressEntityFetchAfterNotFoundRef.current = false;
  }, [id, idCopyFrom]);

  const fetchDto = useEvent(
    async (
      curId: number,
      isDuplicateMode?: boolean,
    ): Promise<SimpleResult<RequestBodyModel>> => {
      try {
        suppressEntityFetchAfterNotFoundRef.current = false;
        setCrudServerErrors(undefined);
        setApiServerErrors(undefined);
        setLoading(true);

        const dtoFromApi = await CrudApi.getById<T>(entity, curId);
        Log.debug(
          `CRUD dto received for id ${curId}, idCopyFrom: ${idCopyFrom}` +
            ` duplicate:${logBoolean(isDuplicateMode)}, isEdit:${logBoolean(
              isEdit,
            )},` +
            ` mode: ${mode}`,
          dtoFromApi,
        );

        afterFetchCrudDtoCallback({
          entity,
          dtoFromApi,
        });

        if (isDuplicateMode) {
          const duplicatedDto = processDuplicationDto(dtoFromApi);
          Log.debug("Set editing DUPLICATE dto from api, ", duplicatedDto);
          setDto(duplicatedDto);
          return createSuccessResult(duplicatedDto);
        } else {
          const apiDtoProcessed = processDtoFromApi(dtoFromApi);
          setDto(apiDtoProcessed);
          setInitialDataSnapshotDto(apiDtoProcessed);

          if (isRideEntity(entity)) {
            updateRidePreviousAreasSnapshot(
              apiDtoProcessed as Partial<IRideDto>,
            );
            updateRidePreviousMetadataSnapshot(
              apiDtoProcessed as Partial<IRideDto>,
            );
          }

          return createSuccessResult(apiDtoProcessed);
        }
      } catch (e) {
        if ((e as ApiException).statusCode === HttpStatusCode.NotFound) {
          if (mode !== EditFormMode.CrudRoute) {
            suppressEntityFetchAfterNotFoundRef.current = true;
            setDto(undefined);
            setInitialDataSnapshotDto(undefined);
            const unavailableMsg = t(errors.common.recordNotAvailable);
            setCrudServerErrors([unavailableMsg]);
            return createErrorResultFromException([unavailableMsg]);
          }
          navigatePageNotFound();
          return createErrorResult(
            errors.common.pageNotFound,
            `Page for ${entity} not found by id ${curId}`,
          );
        }

        if ((e as ApiException).statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = warnAndToastException({
            error: e,
            fallbackTranslationKey: errors.common.dontHavePermissionForAction,
            translationFunction: t,
            logWarnMessage: `Forbidden: no permission for ${entity}`,
          });
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
            debugInfo: e,
          });
        }

        const errorsNormalized = normalizeErrors(e as IApiError, t);

        Log.warnAndToast(
          transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
        );

        setCrudServerErrors(errorsNormalized);
        return createErrorResultFromException(errorsNormalized);
      } finally {
        setLoading(false);
      }
    },
  );

  const fetchData = useCallback(
    ({
      idFetch,
      isDuplicateMode,
    }: {
      idFetch: number;
      isDuplicateMode?: boolean;
    }) => {
      if (!idFetch || mode === EditFormMode.DropdownAddDrawer) {
        return;
      }

      fetchDto(idFetch, isDuplicateMode);
    },
    [fetchDto, mode],
  );

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  // Show loader immediately on record switch, before the fetch effect runs.
  useEffect(() => {
    if (!isRoutePresent || !isEdit || !id || idCopyFrom) {
      return;
    }

    if (dto && dto.id !== id) {
      setLoading(true);
    }
  }, [dto, id, idCopyFrom, isEdit, isRoutePresent, setLoading]);

  useEffect(() => {
    if (!isRoutePresent || isLoading) {
      return;
    }

    if (
      suppressEntityFetchAfterNotFoundRef.current &&
      mode !== EditFormMode.CrudRoute
    ) {
      return;
    }

    /* fetch when dto is missing, belongs to another record, or is a create/duplicate template without id */
    const isDtoRequiredFetch = !dto || dto.id !== id;

    if (!!id && !idCopyFrom && isDtoRequiredFetch) {
      Log.debug(
        `Crud fetch called for id:${id}, dto.id: ${dto?.id || NO_VALUE_DISPLAY}`,
      );
      fetchData({ idFetch: id, isDuplicateMode: false });
    }
  }, [dto, fetchData, id, idCopyFrom, isLoading, isRoutePresent, mode]);

  useEffect(() => {
    if (!isRoutePresent || isLoading) {
      return;
    }

    if (
      suppressEntityFetchAfterNotFoundRef.current &&
      mode !== EditFormMode.CrudRoute
    ) {
      return;
    }

    /*  only on page refresh on /add/:id url we need loading duplicate dto from api
   (in regular cases editing dto is processed and used for duplicate directly, without fetching from api) */
    if (!dto && !!idCopyFrom) {
      Log.debug(
        `Crud duplicate fetch called,idCopyFrom: ${idCopyFrom} dto.id:${dto?.id || NO_VALUE_DISPLAY}`,
      );
      fetchData({ idFetch: idCopyFrom, isDuplicateMode: true });
    }
  }, [dto, fetchData, idCopyFrom, isLoading, isRoutePresent, mode]);

  useEffect(() => {
    if (!isRoutePresent || !idCopyFrom || isLoading || !dto?.id) {
      return;
    }

    Log.debug(`Strip source dto id ${dto.id} for duplicate of ${idCopyFrom}`);
    setDto(processDuplicationDto(dto) as T);
  }, [
    dto,
    idCopyFrom,
    isLoading,
    isRoutePresent,
    processDuplicationDto,
    setDto,
  ]);

  const save = useEvent(
    async (updatedDto: Partial<T>): Promise<SimpleResult<T>> => {
      if (!updatedDto || isInvalidCrudEditDtoId) {
        return new SimpleResult<T>({
          success: false,
          error: t("errors.invalidDTOToSaveSkipSendToServer"),
        });
      }

      try {
        setCrudServerErrors(undefined);
        setApiServerErrors(undefined);
        setLoading(true);
        Log.debug(
          `${isEdit ? "Update" : "Create"} entity from dto ${
            isEdit ? "(id:" + Number(id) + ")" : ""
          }:`,
          updatedDto,
        );
        const savedDto = isEdit
          ? await CrudApi.update(entity, Number(id), updatedDto)
          : await CrudApi.create(entity, updatedDto);
        Log.debug(
          `dto ${isEdit ? "updated " : "created"} successfully:`,
          savedDto,
        );

        if (savedDto?.id) {
          const processedSavedDto = processDtoFromApi(savedDto);
          setInitialDataSnapshotDto(processedSavedDto);
          setHighlightedRows([savedDto.id]);
        }

        updateMetadataResolve(savedDto?.metadata);

        startTransition(() => {
          refreshDropdownItemsForEntity({ entity, getDropdownItems });
        });

        afterSaveCallback(entity);

        return createSuccessResult(savedDto);
      } catch (err) {
        if ((err as ApiException).statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = warnAndToastException({
            error: err,
            fallbackTranslationKey: errors.common.dontHavePermissionForAction,
            translationFunction: t,
          });
          return new SimpleResult<T>({
            error: [translateInfoItem(errorDetails, t)],
            errors: (err as IApiError).errors as IValidationError[],
          });
        }

        const errorsNormalized = normalizeErrors(err as IApiError, t);

        Log.warnAndToast(
          transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
        );

        setCrudServerErrors(prevErrors => [
          ...(prevErrors || []),
          ...errorsNormalized,
        ]);

        return new SimpleResult<T>({
          error: errorsNormalized,
          errors: (err as IApiError).errors as IValidationError[],
        });
      } finally {
        setLoading(false);

        if (isEntityServerSideFiltered(entity)) {
          setIsNeedFetch(true);
        }
      }
    },
  );

  const handleArchiveError = useEvent(
    (err: unknown): SimpleResult<RequestBodyModel> => {
      if ((err as ApiException).statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = warnAndToastException({
          error: err,
          fallbackTranslationKey: errors.common.dontHavePermissionForAction,
          translationFunction: t,
        });
        const errorMessage = translateInfoItem(errorDetails, t);
        return new SimpleResult({
          error: [errorMessage],
          data: {
            errorMessage,
            isDeleteRelationConfirm: false,
          },
        });
      }

      const isDeleteRelationConfirm =
        ENTITY_DELETE_RELATIONS_CONFIRM_LIST.includes(entity);
      const isArchiveRelationError =
        isTranslatableItem(err) &&
        err.translationKey === errors.common.archiveRelationError;

      const errorsNormalized = normalizeReferencedEntitiesErrors(
        err as IApiError,
        entity,
        i18n,
        t,
      );

      if (!isDeleteRelationConfirm) {
        Log.warnAndToast(
          transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
        );

        setCrudServerErrors(errorsNormalized);
        setArchiveRelationError(isArchiveRelationError);
      }

      return new SimpleResult({
        error: errorsNormalized,
        data: {
          errorMessage: errorsNormalized?.[0] ?? "",
          isDeleteRelationConfirm,
        },
      });
    },
  );

  const archive = useEvent(
    async (): Promise<SimpleResult<RequestBodyModel>> => {
      if (isInvalidCrudEditDtoId) {
        return new SimpleResult({
          success: false,
          error: t("errors.invalidIdForArchiving"),
        });
      }

      try {
        setCrudServerErrors(undefined);
        setArchiveRelationError(false);
        setApiServerErrors(undefined);
        setLoading(true);
        const data = await CrudApi.delete(entity, Number(id));
        removeStarredIds([Number(id)]);
        startTransition(() => {
          refreshDropdownItemsForEntity({ entity, getDropdownItems });
        });
        Log.successAndToast(t("general.RECORD_ARCHIVED_SUCCESSFULLY"));
        return createSuccessResult(data);
      } catch (err) {
        return handleArchiveError(err);
      } finally {
        setLoading(false);
      }
    },
  );

  const archiveWithRelation = useEvent(
    async (): Promise<SimpleResult<RequestBodyModel>> => {
      if (isInvalidCrudEditDtoId) {
        return new SimpleResult({
          success: false,
          error: t("errors.invalidIdForArchiving"),
        });
      }

      try {
        setCrudServerErrors(undefined);
        setArchiveRelationError(false);
        setApiServerErrors(undefined);
        setLoading(true);
        const data = await CrudApi.deleteWithParams(
          entity,
          Number(id),
          DELETE_ENTITY_WITH_RELATION_PAYLOAD,
        );
        removeStarredIds([Number(id)]);
        startTransition(() => {
          refreshDropdownItemsForEntity({ entity, getDropdownItems });
        });
        Log.successAndToast(t("general.RECORD_ARCHIVED_SUCCESSFULLY"));
        return createSuccessResult(data);
      } catch (err) {
        if ((err as ApiException).statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = warnAndToastException({
            error: err,
            fallbackTranslationKey: errors.common.dontHavePermissionForAction,
            translationFunction: t,
          });
          return new SimpleResult({
            error: [translateInfoItem(errorDetails, t)],
          });
        }

        const errorsNormalized = normalizeErrors(err as IApiError, t);

        Log.warnAndToast(
          transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
        );

        setCrudServerErrors(errorsNormalized);

        return new SimpleResult({ error: errorsNormalized });
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    isEdit,
    isDuplicate,
    dto: dto as T,
    save,
    archive,
    archiveWithRelation,
    onEditFormClose,
    fetchDto,
    setDto,
    processDuplicationDto,
  };
};
